import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Flex, rem, Spinner, Text } from '@bonusly/design-system';

import { CustomVideoPlayer } from '../../video/custom-video-player';
import { Video, VideoStatus } from '../types/announcements';

interface AnnouncementVideoProps {
	video: Video;
}

export const AnnouncementVideo: FC<AnnouncementVideoProps> = ({ video }) => {
	const { t } = useTranslation('announcements', { keyPrefix: 'carousel' });
	const videoElementRef = useRef<HTMLVideoElement | null>(null);

	const captions = video.video_transcript_url ? [{ src: video.video_transcript_url, label: 'English', srcLang: 'en' }] : undefined;
	const playableSource = video.video_hd_url || video.video_sd_url || video.video_original_url;

	// If we have any playable source, render the player even if some sub-step failed (e.g. captions).
	// Only fall back to a placeholder when there's nothing to play yet.
	if (playableSource) {
		return (
			<Box mt={3} w="100%" position="relative" data-testid="announcement-video">
				<CustomVideoPlayer
					videoId={video.video_id}
					poster={video.video_poster_url || undefined}
					ref={videoElementRef}
					captions={captions}
					condenseVideoControlsForMobile
					defaultMuted
					borderRadius="sm"
					maxH={rem(400)}
				>
					{video.video_hd_url && <source data-target="hd" src={video.video_hd_url} />}
					{video.video_sd_url && <source data-target="sd" src={video.video_sd_url} />}
					{video.video_original_url && <source data-target="original" src={video.video_original_url} />}
					{/* CustomVideoPlayer renders the <track> from the captions prop above; do not duplicate it here. */}
				</CustomVideoPlayer>
			</Box>
		);
	}

	if (video.status === VideoStatus.Failed) {
		return (
			<Box mt={3} w="100%" borderRadius="sm" backgroundColor="bg.subtle" p={6} textAlign="center" data-testid="announcement-video-failed">
				<Text fontSize={50} color="error.fg">
					{t('video_failed')}
				</Text>
			</Box>
		);
	}

	return (
		<Box mt={3} w="100%" borderRadius="sm" backgroundColor="bg.subtle" p={6} textAlign="center" data-testid="announcement-video-processing">
			<Flex direction="column" alignItems="center" gap={2}>
				<Spinner size="md" />
				<Text fontSize={50} color="text.softer">
					{t('video_processing')}
				</Text>
			</Flex>
		</Box>
	);
};
